import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import QuoteBuilder from "@/components/QuoteBuilder";
import QuoteCard from "@/components/QuoteCard";
import SafeAvatar from "@/components/SafeAvatar";

export default function ThreadView() {
  const { leadId, partnerId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [partner, setPartner] = useState(null);
  const [leadTitle, setLeadTitle] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/messages/${leadId}`);
    setMsgs(data);
  };

  useEffect(() => {
    load();
    api.get("/messages/threads").then((r) => {
      const t = r.data.find((x) => x.lead_id === leadId);
      if (t) { setPartner(t.partner); setLeadTitle(t.lead_title || ""); }
    }).catch(() => {});
  }, [leadId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { lead_id: leadId, to_user_id: partnerId, body });
      setMsgs((m) => [...m, data]);
      setBody("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  const isPro = user?.role === "pro";

  return (
    <div className="app-shell grain-bg flex flex-col h-screen">
      <header className="px-4 py-3 flex items-center gap-3 border-b border-border bg-white">
        <button onClick={() => nav(-1)} className="tap-scale" data-testid="thread-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        {partner && <SafeAvatar src={partner.avatar} role={partner.role} name={partner.name} className="w-10 h-10 rounded-xl object-cover" />}
        <div className="flex-1 min-w-0">
          <h2 className="font-heading font-bold text-base text-ink truncate" data-testid="thread-partner-name">{partner?.name || "Conversation"}</h2>
          <div className="text-[11px] text-slate2 truncate" data-testid="thread-lead-title">{leadTitle || "Lead privé"}</div>
        </div>
        {isPro && (
          <button
            onClick={() => setQuoteOpen(true)}
            className="rounded-full bg-pro/10 text-pro px-3 h-9 text-xs font-semibold flex items-center gap-1.5 tap-scale"
            data-testid="thread-open-quote-btn"
          >
            <FileText className="w-3.5 h-3.5" /> Devis
          </button>
        )}
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5">
        {msgs.length === 0 && (
          <div className="text-center text-sm text-slate2 mt-8">
            Démarrez la conversation par un message clair et bienveillant.
          </div>
        )}
        {(() => {
          const seenQuoteRefs = new Set();
          return msgs.map((m) => {
            const mine = m.from_user_id === user.id;
            if (m.quote_ref) {
              if (seenQuoteRefs.has(m.quote_ref)) {
                // Message de suivi (accept/reject) — afficher juste comme un message texte simple
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-pro text-white rounded-br-md" : "bg-white border border-border text-ink rounded-bl-md"}`}>
                      {m.body}
                    </div>
                  </div>
                );
              }
              seenQuoteRefs.add(m.quote_ref);
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <QuoteCard
                    quoteId={m.quote_ref}
                    currentRole={user.role}
                  />
                </div>
              );
            }
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-pro text-white rounded-br-md" : "bg-white border border-border text-ink rounded-bl-md"}`}>
                  {m.body}
                </div>
              </div>
            );
          });
        })()}
        <div ref={endRef} />
      </div>

      <div className="border-t border-border bg-white p-3 flex items-center gap-2">
        <Input
          value={body} onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Votre message…"
          className="rounded-full h-11"
          data-testid="thread-input"
        />
        <Button onClick={send} disabled={sending || !body.trim()} className="rounded-full h-11 w-11 p-0 bg-brand hover:bg-brand-hover" data-testid="thread-send-btn">
          <Send className="w-4 h-4" />
        </Button>
      </div>

      {isPro && (
        <QuoteBuilder
          open={quoteOpen}
          onOpenChange={setQuoteOpen}
          leadId={leadId}
          leadTitle={leadTitle}
          onCreated={() => load()}
        />
      )}
    </div>
  );
}
